import { useId } from 'react'
import type { ListPageSize } from '../../Hooks/useListPagination'
import './ListPagination.scss'

interface ListPaginationProps {
  page: number
  limit: number
  total: number | null
  hasNextPage?: boolean
  disabled?: boolean
  onPageChange: (page: number) => void
  onPageSizeChange?: (limit: ListPageSize) => void
}

const getPageNumbers = (page: number, totalPages: number): (number | string)[] => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1)
  if (page <= 3) return [1, 2, 3, 4, '...', totalPages]
  if (page >= totalPages - 2)
    return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
  return [1, '...', page - 1, page, page + 1, '...', totalPages]
}

const ListPagination = ({
  page,
  limit,
  total,
  hasNextPage = false,
  disabled = false,
  onPageChange,
  onPageSizeChange,
}: ListPaginationProps) => {
  const pageSizeId = useId()
  const totalPages = total === null ? null : Math.max(1, Math.ceil(total / limit))
  const canGoNext = totalPages === null ? hasNextPage : page < totalPages

  return (
    <div className='admin-list-pagination'>
      <div className='admin-list-pagination__size'>
        {onPageSizeChange ? (
          <label htmlFor={pageSizeId}>Rows per page</label>
        ) : (
          <span>Rows per page</span>
        )}
        {onPageSizeChange ? (
          <select
            id={pageSizeId}
            value={limit}
            disabled={disabled}
            onChange={(event) => {
              const value = Number(event.target.value)
              if (value === 20 || value === 50) onPageSizeChange(value)
            }}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        ) : (
          <span>{limit}</span>
        )}
      </div>
      <nav className='admin-list-pagination__controls' aria-label='Table pagination'>
        <button
          type='button'
          disabled={disabled || page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          &larr; Previous
        </button>
        <div className='admin-list-pagination__numbers'>
          {totalPages === null ? (
            <span>Page {page}</span>
          ) : (
            getPageNumbers(page, totalPages).map((item, index) =>
              typeof item === 'number' ? (
                <button
                  key={item}
                  type='button'
                  aria-label={`Page ${item}`}
                  aria-current={page === item ? 'page' : undefined}
                  disabled={disabled}
                  onClick={() => onPageChange(item)}
                >
                  {item}
                </button>
              ) : (
                <span key={`gap-${index}`} aria-hidden='true'>
                  {item}
                </span>
              ),
            )
          )}
        </div>
        <button
          type='button'
          disabled={disabled || !canGoNext}
          onClick={() => onPageChange(page + 1)}
        >
          Next &rarr;
        </button>
      </nav>
    </div>
  )
}

export default ListPagination
